import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CreditCard, RefreshCw, AlertCircle, Clock, Ban, Mail, CheckCircle } from "lucide-react";

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-100 mb-6">
            <CreditCard className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Refund & Cancellation Policy</h1>
          <p className="text-slate-500">Last Updated: December 2024</p>
        </div>

        {/* Policy Content */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-8 md:p-12 space-y-10">
            
            {/* 1. Nature of Digital Services */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">1</span>
                Nature of Digital Services
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  <strong>CLARIAS</strong> provides digital, AI-based subscription services for UPSC preparation. Our services are delivered entirely online and include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AI-powered doubt resolution and explanations</li>
                  <li>Practice tests and MCQ practice</li>
                  <li>Performance analytics and progress tracking</li>
                  <li>Answer evaluation features</li>
                  <li>Current affairs and study materials</li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                  <p className="text-amber-800 font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Important
                  </p>
                  <p className="text-amber-700 mt-2">
                    Access to subscription features is granted <strong>immediately</strong> after successful payment. Once access is granted, the service is considered delivered.
                  </p>
                </div>
              </div>
            </section>

            {/* 2. Refund Eligibility */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">2</span>
                Refund Eligibility
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-800 font-semibold text-lg">
                    Digital services once accessed are non-refundable.
                  </p>
                </div>
                
                <p className="mt-4">Due to the immediate-access nature of our digital services, refunds are generally <strong>NOT</strong> available for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Subscriptions where access has been granted and used</li>
                  <li>Dissatisfaction with AI-generated content or features</li>
                  <li>Exam results, ranks, or selection outcomes</li>
                  <li>Partial usage or unused subscription period</li>
                  <li>Change of mind after purchase</li>
                  <li>Failure to use the subscription within the validity period</li>
                </ul>
                <p className="mt-4 text-slate-500 italic">
                  By completing a purchase, you acknowledge and agree to this no-refund policy for accessed digital services.
                </p>
              </div>
            </section>

            {/* 3. Limited Refund Exceptions */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">3</span>
                Limited Refund Exceptions
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>Refunds may be considered <strong>ONLY</strong> in the following exceptional circumstances:</p>
                
                <div className="space-y-3 mt-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-emerald-800">Payment Deducted, Access Not Granted</p>
                      <p className="text-sm text-emerald-700 mt-1">If payment was successfully deducted but subscription access was not provided due to a technical issue on our end.</p>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-emerald-800">Verified Technical Failure</p>
                      <p className="text-sm text-emerald-700 mt-1">If a verified technical failure from CLARIAS' side prevented you from accessing paid features entirely.</p>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-emerald-800">Duplicate Payment</p>
                      <p className="text-sm text-emerald-700 mt-1">If you were charged multiple times for the same subscription due to a payment processing error.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-100 rounded-xl p-4 mt-4">
                  <p className="font-medium text-slate-800">Conditions for Exception Requests:</p>
                  <ul className="list-disc pl-6 mt-2 text-sm text-slate-600 space-y-1">
                    <li>Request must be submitted within <strong>48 hours</strong> of the transaction</li>
                    <li>All requests are reviewed on a <strong>case-by-case basis</strong></li>
                    <li>Supporting evidence (screenshots, transaction IDs) may be required</li>
                    <li>CLARIAS reserves the right to approve or deny refund requests at its discretion</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 4. Cancellation */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">4</span>
                Subscription Cancellation
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>For subscriptions with auto-renewal:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You may <strong>cancel auto-renewal</strong> at any time through your account settings or by contacting support</li>
                  <li>Cancellation will <strong>stop future billing</strong> cycles from being charged</li>
                  <li>Your access will continue until the end of the current paid period</li>
                  <li><strong>No refund</strong> will be provided for the current or remaining billing period</li>
                </ul>
                <p className="mt-4">
                  To cancel your subscription, please visit your account settings or contact <a href="mailto:support@clarias.in" className="text-amber-600 font-medium hover:underline">support@clarias.in</a>.
                </p>
              </div>
            </section>

            {/* 5. Refund Processing */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">5</span>
                Refund Processing
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>If a refund is approved under the limited exceptions:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Refunds will be processed to the <strong>original payment method</strong> used for the transaction</li>
                  <li>Processing time: <strong>7–10 business days</strong> from approval</li>
                  <li>Actual credit to your account may take additional time depending on your bank or payment provider</li>
                </ul>
                <div className="bg-slate-100 rounded-xl p-4 mt-4 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-600">
                    CLARIAS is not responsible for delays caused by banks, payment gateways, or other financial institutions in processing refunds.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. Abuse Prevention */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">6</span>
                Abuse Prevention
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>To maintain the integrity of our services and protect against fraud:</p>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4 flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">CLARIAS reserves the right to deny refund requests in cases of:</p>
                    <ul className="list-disc pl-6 mt-2 text-sm text-red-700 space-y-1">
                      <li>Suspected misuse or abuse of the refund process</li>
                      <li>Violation of Terms of Service</li>
                      <li>Repeated refund requests from the same user</li>
                      <li>Fraudulent chargebacks or payment disputes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* 7. Policy Changes */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">7</span>
                Policy Changes
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  CLARIAS reserves the right to modify this Refund & Cancellation Policy at any time. Changes will be effective immediately upon posting to the platform.
                </p>
                <p>
                  Any modifications will apply to <strong>future transactions only</strong>. Transactions completed before the policy change will be governed by the policy in effect at the time of purchase.
                </p>
              </div>
            </section>

            {/* 8. Contact */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">8</span>
                Contact Us
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>For refund requests, cancellation inquiries, or questions about this policy, please contact us at:</p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-600" />
                  <a href="mailto:support@clarias.in" className="text-amber-600 font-medium hover:underline">
                    support@clarias.in
                  </a>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Please include your registered email, transaction ID, and a detailed description of your request for faster processing.
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/terms-of-service" className="text-amber-600 hover:underline text-sm">Terms of Service</Link>
          <span className="text-slate-300">|</span>
          <Link to="/privacy-policy" className="text-amber-600 hover:underline text-sm">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
